import api, { unwrap } from './client';
import { getApiBaseUrl } from '@/config/env';
import type { ApiResponse } from '@/types/api';
import type {
  ChangePasswordRequest,
  ForgotPasswordRequest,
  LoginRequest,
  LoginResponse,
  MfaCodeRequest,
  MfaConfirmRequest,
  MfaSetupResponse,
  MfaStatusResponse,
  MfaVerifyRequest,
  RefreshTokenRequest,
  ResetPasswordRequest,
  SsoExchangeRequest,
} from '@/types/auth';

export interface OrganizationRegisterRequest {
  organization: import('@/types/organization').OrganizationRequest;
  ownerEmail: string;
  ownerPassword: string;
  ownerFirstName: string;
  ownerLastName?: string;
  deviceId?: string;
}

export interface ExistingAccountPreview {
  exists: boolean;
  organizationName?: string;
  enabledModules?: string[];
  requestedModule?: string;
  moduleAlreadyEnabled?: boolean;
}

export interface RegisterAccountCheck extends ExistingAccountPreview {
  passwordValid: boolean;
}

export interface RegisterAccountCheckRequest {
  email: string;
  password: string;
  module?: string;
}

export interface AddApplicationRequest {
  email: string;
  password: string;
  module: string;
  deviceId?: string;
}

export const authApi = {
  register: (body: OrganizationRegisterRequest) =>
    unwrap(api.post<ApiResponse<LoginResponse>>('/v1/auth/register', body)),

  previewExistingAccount: (email: string, module?: string) =>
    unwrap(
      api.get<ApiResponse<ExistingAccountPreview>>('/v1/auth/register/preview', {
        params: { email, module },
      }),
    ),

  checkRegisterAccount: (body: RegisterAccountCheckRequest) =>
    unwrap(api.post<ApiResponse<RegisterAccountCheck>>('/v1/auth/register/check', body)),

  addApplication: (body: AddApplicationRequest) =>
    unwrap(api.post<ApiResponse<LoginResponse>>('/v1/auth/register/add-application', body)),

  login: (body: LoginRequest) =>
    unwrap(api.post<ApiResponse<LoginResponse>>('/v1/auth/login', body)),

  session: () => unwrap(api.get<ApiResponse<LoginResponse>>('/v1/auth/session')),

  refresh: (body?: RefreshTokenRequest) =>
    unwrap(api.post<ApiResponse<LoginResponse>>('/v1/auth/refresh', body ?? {})),

  logout: () => unwrap(api.post<ApiResponse<null>>('/v1/auth/logout')),

  forgotPassword: (body: ForgotPasswordRequest) =>
    unwrap(api.post<ApiResponse<null>>('/v1/auth/forgot-password', body)),

  resetPassword: (body: ResetPasswordRequest) =>
    unwrap(api.post<ApiResponse<null>>('/v1/auth/reset-password', body)),

  changePassword: (body: ChangePasswordRequest) =>
    unwrap(api.post<ApiResponse<null>>('/v1/auth/change-password', body)),

  verifyMfa: (body: MfaVerifyRequest) =>
    unwrap(api.post<ApiResponse<LoginResponse>>('/v1/auth/mfa/verify', body)),

  mfaStatus: () => unwrap(api.get<ApiResponse<MfaStatusResponse>>('/v1/auth/mfa/status')),

  mfaSetup: () => unwrap(api.get<ApiResponse<MfaSetupResponse>>('/v1/auth/mfa/setup')),

  mfaConfirm: (body: MfaConfirmRequest) =>
    unwrap(api.post<ApiResponse<null>>('/v1/auth/mfa/confirm', body)),

  mfaDisable: (body: MfaCodeRequest) =>
    unwrap(api.post<ApiResponse<null>>('/v1/auth/mfa/disable', body)),

  ssoProviders: () =>
    unwrap(api.get<ApiResponse<Record<string, boolean>>>('/v1/auth/sso/providers')),

  ssoExchange: (body: SsoExchangeRequest) =>
    unwrap(api.post<ApiResponse<LoginResponse>>('/v1/auth/sso/exchange', body)),

  ssoAuthorizeUrl: (
    provider: 'google' | 'microsoft',
    options?: { redirectAfter?: string; entryModule?: string; registerIntent?: boolean },
  ) => {
    const params = new URLSearchParams();
    if (options?.redirectAfter) params.set('redirectAfter', options.redirectAfter);
    if (options?.entryModule) params.set('entryModule', options.entryModule);
    if (options?.registerIntent) params.set('registerIntent', 'true');
    const q = params.toString();
    const base = `${getApiBaseUrl()}/v1/auth/sso/${provider}/authorize`;
    return q ? `${base}?${q}` : base;
  },
};
